import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {fetchPricesRegionSuggestions} from "../api/fetch_prices_region_suggestions";
import {getPricesAddressSearchCacheKey, pricesAddressSearchCache, rcnDirectAddressSource} from "../api/prices_address_source";
import {PRICES_SEARCH_REGIONS_EXPANDED_PAGE_SIZE, RCN_ADDRESSES_MAX_LIMIT} from "../constants/prices_search";
import type {
    IPricesAddressSearchResult,
    IPricesAddressSource,
    IPricesRegionSuggestionsResult,
    IPricesScopedAddressResult,
    IPricesSearchStreetItem
} from "../types/IPricesSearchModel";
import {getPricesSearchCityScopeKey, getStreetScopedQuery} from "../utils/prices_search/group_rcn_addresses";

const REGIONS_SCOPE_KEY = "regions";

export interface IPricesSearchShowMoreCityScope {
    citySlug: string;
    /** `getCityScopedQuery(parsed, cityName)`; null skips the city (the query would repeat the base one). */
    query: string | null;
}

export interface IUsePricesSearchShowMoreOptions {
    /** `parsed.baseKey` of the current phrase, null below the minimum length. A change drops everything in flight. */
    baseKey: string | null;
    source?: IPricesAddressSource;
}

export interface IUsePricesSearchShowMoreResult {
    /** Scoped address results of the current `baseKey`, for `buildPricesSearchModel({scoped})`. */
    scoped: readonly IPricesScopedAddressResult[];
    /** The region list re-queried with the expanded page size, for the current `baseKey`; null until it answers. */
    expandedRegions: IPricesRegionSuggestionsResult | null;
    /** Show-more requests the user is waiting for - section kinds and street keys (`IPricesSearchUiState.loadingShowMoreKeys`). */
    loadingKeys: readonly string[];
    /**
     * Show-more requests the user asked for that ended with no result (failed, or cancelled by closing the panel) -
     * the caller collapses them back to their link, so clicking it again retries. A new request for the key clears it.
     */
    failedKeys: readonly string[];
    /**
     * All numbers of a street (`q="<city> <street>"`). `silent` is the prefetch of the expanded street: it shows no
     * spinner, but a later explicit request for the same street turns the spinner on while it is still running.
     */
    loadStreetNumbers: (street: Pick<IPricesSearchStreetItem, "rcn" | "streetKey">, options?: {silent?: boolean}) => void;
    /** "Pokaż więcej ulic|adresów" over a capped base response: one `q="<city> <words>"` request per city. */
    loadCityStreets: (loadingKey: string, cities: readonly IPricesSearchShowMoreCityScope[]) => void;
    /** "Pokaż więcej dzielnic": the region search again with the expanded page size. */
    loadDistricts: (query: string) => void;
    /** Cancels every request in flight (× in the field, closing the panel). Results already in stay. */
    abortAll: () => void;
}

interface IInFlightRequest {
    baseKey: string;
    scopeKey: string;
    /** Null for a silent prefetch. */
    loadingKey: string | null;
    controller: AbortController;
}

interface IShowMoreState {
    scoped: IPricesScopedAddressResult[];
    regions: {baseKey: string; result: IPricesRegionSuggestionsResult} | null;
    /** `${baseKey}|${loadingKey}` of the requests the user waits for; a key may repeat (one per city). */
    loading: string[];
    /** Loading entries whose requests all ended with nothing: rejected, or aborted while the user waited. */
    failed: string[];
    /** Loading entries with at least one answer while their other requests still run (one city of several answered). */
    answered: string[];
}

const EMPTY_STATE: IShowMoreState = {scoped: [], regions: null, loading: [], failed: [], answered: []};

const toLoadingEntry = (baseKey: string, loadingKey: string): string => `${baseKey}\u0000${loadingKey}`;

const removeOnce = (list: string[], value: string): string[] => {
    const index = list.indexOf(value);

    return index === -1 ? list : [...list.slice(0, index), ...list.slice(index + 1)];
};

const addUnique = (list: string[], value: string): string[] => (list.includes(value) ? list : [...list, value]);
const without = (list: string[], value: string): string[] => (list.includes(value) ? list.filter((item) => item !== value) : list);

// The keys of the current phrase in a list of loading entries.
const getKeysOf = (entries: readonly string[], baseKey: string | null): string[] => {
    if (baseKey === null) {
        return [];
    }

    const prefix = toLoadingEntry(baseKey, "");

    return [...new Set(entries.filter((entry) => entry.startsWith(prefix)).map((entry) => entry.slice(prefix.length)))];
};

/**
 * "Pokaż więcej" and building-number re-queries, stored with the `(baseKey, scopeKey)` they were asked
 * for - the model only accepts results of the current `baseKey`, so a slow answer for an older phrase never lands in
 * the list. A new phrase, `abortAll()` and unmounting cancel what is in flight; the address answers go through the
 * shared address cache, so asking again for the same scope costs no request. A request the user asked for that ends
 * with nothing - every request of its key failed, or it was cancelled - lands in `failedKeys`.
 */
export const usePricesSearchShowMore = (options: IUsePricesSearchShowMoreOptions): IUsePricesSearchShowMoreResult => {
    const {baseKey, source = rcnDirectAddressSource} = options;
    const [state, setState] = useState<IShowMoreState>(EMPTY_STATE);
    const inFlightRef = useRef<IInFlightRequest[]>([]);
    // Read by the stable loaders; updated before the effects of the component using the hook run.
    const baseKeyRef = useRef(baseKey);
    const sourceRef = useRef(source);

    useEffect(() => {
        baseKeyRef.current = baseKey;
        sourceRef.current = source;
    });

    const abortAll = useCallback(() => {
        const inFlight = inFlightRef.current;
        inFlightRef.current = [];
        inFlight.forEach((request) => request.controller.abort());
        // What the user was waiting for got nothing: its section goes back to the link, for another try.
        const aborted = inFlight.flatMap((request) => (request.loadingKey === null ? [] : [toLoadingEntry(request.baseKey, request.loadingKey)]));
        setState((previous) =>
            previous.loading.length === 0 && aborted.length === 0
                ? previous
                : {...previous, loading: [], answered: [], failed: aborted.reduce(addUnique, previous.failed)}
        );
    }, []);

    // A new phrase starts from nothing: older results would be ignored by the model anyway, requests are cancelled.
    useEffect(() => {
        abortAll();
        setState((previous) => (previous === EMPTY_STATE ? previous : EMPTY_STATE));
    }, [baseKey, abortAll]);

    useEffect(() => abortAll, [abortAll]);

    /** Starts a request unless the same scope is already known or on its way; returns false when nothing was started. */
    const startRequest = useCallback(
        <T>(
            scopeKey: string,
            loadingKey: string | null,
            run: (signal: AbortSignal) => Promise<T>,
            onResult: (result: T, requestBaseKey: string) => IShowMoreState | ((previous: IShowMoreState) => IShowMoreState)
        ) => {
            const requestBaseKey = baseKeyRef.current;

            if (requestBaseKey === null) {
                return;
            }

            const running = inFlightRef.current.find((request) => request.baseKey === requestBaseKey && request.scopeKey === scopeKey);

            if (running) {
                // A prefetch the user now asked for explicitly: show its spinner from here on.
                if (running.loadingKey === null && loadingKey !== null) {
                    running.loadingKey = loadingKey;
                    const entry = toLoadingEntry(requestBaseKey, loadingKey);
                    setState((previous) => ({...previous, loading: [...previous.loading, entry], failed: without(previous.failed, entry)}));
                }
                return;
            }

            const request: IInFlightRequest = {baseKey: requestBaseKey, scopeKey, loadingKey, controller: new AbortController()};
            const {signal} = request.controller;
            inFlightRef.current = [...inFlightRef.current, request];

            if (loadingKey !== null) {
                // A retry clears the failure of the previous attempt.
                const entry = toLoadingEntry(requestBaseKey, loadingKey);
                setState((previous) => ({...previous, loading: [...previous.loading, entry], failed: without(previous.failed, entry)}));
            }

            const settle = (update: ((previous: IShowMoreState) => IShowMoreState) | null) => {
                // Aborted (new phrase, abortAll, unmount): the abort already cleaned up after it.
                if (signal.aborted || !inFlightRef.current.includes(request)) {
                    return;
                }

                inFlightRef.current = inFlightRef.current.filter((item) => item !== request);
                setState((previous) => {
                    const next = update ? update(previous) : previous;
                    // Read at settle time: a silent prefetch the user asked for in the meantime counts as asked for.
                    const doneLoadingKey = request.loadingKey;

                    if (doneLoadingKey === null) {
                        return next;
                    }

                    const entry = toLoadingEntry(requestBaseKey, doneLoadingKey);
                    const loading = removeOnce(next.loading, entry);
                    const answered = update ? addUnique(next.answered, entry) : next.answered;

                    // Other cities of the same "Pokaż więcej" still run: the outcome is decided by the last one.
                    if (loading.includes(entry)) {
                        return {...next, loading, answered};
                    }

                    // One answer is enough to keep the section expanded; none collapses it back to its link.
                    const isAnswered = answered.includes(entry);

                    return {
                        ...next,
                        loading,
                        answered: without(answered, entry),
                        failed: isAnswered ? without(next.failed, entry) : addUnique(next.failed, entry)
                    };
                });
            };

            run(signal).then(
                (result) => {
                    const update = onResult(result, requestBaseKey);
                    settle(typeof update === "function" ? update : () => update);
                },
                // A failed "Pokaż więcej" keeps the rows already shown; the section collapses back to its link for another try.
                () => settle(null)
            );
        },
        []
    );

    const loadAddresses = useCallback(
        (query: string, scopeKey: string, loadingKey: string | null) => {
            const requestBaseKey = baseKeyRef.current;

            if (requestBaseKey === null) {
                return;
            }

            const currentSource = sourceRef.current;
            const cacheKey = getPricesAddressSearchCacheKey(currentSource, query, RCN_ADDRESSES_MAX_LIMIT);
            const addResult = (result: IPricesAddressSearchResult) => (previous: IShowMoreState) => ({
                ...previous,
                scoped: [
                    ...previous.scoped.filter((item) => !(item.baseKey === requestBaseKey && item.scopeKey === scopeKey)),
                    {...result, baseKey: requestBaseKey, scopeKey}
                ]
            });

            const isKnown = (previous: IShowMoreState) => previous.scoped.some((item) => item.baseKey === requestBaseKey && item.scopeKey === scopeKey);
            const cached = pricesAddressSearchCache.get(cacheKey);

            if (cached !== undefined) {
                setState((previous) => {
                    const next = isKnown(previous) ? previous : addResult(cached)(previous);

                    // A cached city answers for its "Pokaż więcej" while other cities are still asked.
                    return loadingKey === null ? next : {...next, answered: addUnique(next.answered, toLoadingEntry(requestBaseKey, loadingKey))};
                });
                return;
            }

            startRequest(
                scopeKey,
                loadingKey,
                async (signal) => {
                    const result = await currentSource.search(query, {limit: RCN_ADDRESSES_MAX_LIMIT, signal});
                    pricesAddressSearchCache.set(cacheKey, result);

                    return result;
                },
                (result) => addResult(result)
            );
        },
        [startRequest]
    );

    const loadStreetNumbers = useCallback(
        (street: Pick<IPricesSearchStreetItem, "rcn" | "streetKey">, loadOptions: {silent?: boolean} = {}) => {
            const query = getStreetScopedQuery({rawCity: street.rcn.city, rawStreet: street.rcn.street});
            loadAddresses(query, street.streetKey, loadOptions.silent ? null : street.streetKey);
        },
        [loadAddresses]
    );

    const loadCityStreets = useCallback(
        (loadingKey: string, cities: readonly IPricesSearchShowMoreCityScope[]) => {
            for (const city of cities) {
                if (city.query !== null) {
                    loadAddresses(city.query, getPricesSearchCityScopeKey(city.citySlug), loadingKey);
                }
            }
        },
        [loadAddresses]
    );

    const loadDistricts = useCallback(
        (query: string) => {
            startRequest(
                REGIONS_SCOPE_KEY,
                "districts",
                (signal) => fetchPricesRegionSuggestions(query, {pageSize: PRICES_SEARCH_REGIONS_EXPANDED_PAGE_SIZE, signal}),
                (result, requestBaseKey) => (previous) => ({...previous, regions: {baseKey: requestBaseKey, result}})
            );
        },
        [startRequest]
    );

    const loadingKeys = useMemo(() => getKeysOf(state.loading, baseKey), [baseKey, state.loading]);
    const failedKeys = useMemo(() => getKeysOf(state.failed, baseKey), [baseKey, state.failed]);
    const scoped = useMemo(() => state.scoped.filter((item) => item.baseKey === baseKey), [baseKey, state.scoped]);

    return {
        scoped,
        expandedRegions: state.regions && state.regions.baseKey === baseKey ? state.regions.result : null,
        loadingKeys,
        failedKeys,
        loadStreetNumbers,
        loadCityStreets,
        loadDistricts,
        abortAll
    };
};
import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {fetchPricesRegionSuggestions} from "../api/fetch_prices_region_suggestions";
import {getPricesAddressSearchCacheKey, pricesAddressSearchCache, rcnDirectAddressSource} from "../api/prices_address_source";
import {PRICES_SEARCH_REGIONS_EXPANDED_PAGE_SIZE, RCN_ADDRESSES_MAX_LIMIT} from "../constants/prices_search";
import type {
    IPricesAddressSearchResult,
    IPricesAddressSource,
    IPricesRegionSuggestionsResult,
    IPricesScopedAddressResult,
    IPricesSearchStreetItem
} from "../types/IPricesSearchModel";
import {getPricesSearchCityScopeKey, getStreetScopedQuery} from "../utils/prices_search/group_rcn_addresses";

const REGIONS_SCOPE_KEY = "regions";

export interface IPricesSearchShowMoreCityScope {
    citySlug: string;
    /** `getCityScopedQuery(parsed, cityName)`; null skips the city (the query would repeat the base one). */
    query: string | null;
}

export interface IUsePricesSearchShowMoreOptions {
    /** `parsed.baseKey` of the current phrase, null below the minimum length. A change drops everything in flight. */
    baseKey: string | null;
    source?: IPricesAddressSource;
}

export interface IUsePricesSearchShowMoreResult {
    /** Scoped address results of the current `baseKey`, for `buildPricesSearchModel({scoped})`. */
    scoped: readonly IPricesScopedAddressResult[];
    /** The region list re-queried with the expanded page size, for the current `baseKey`; null until it answers. */
    expandedRegions: IPricesRegionSuggestionsResult | null;
    /** Show-more requests the user is waiting for - section kinds and street keys (`IPricesSearchUiState.loadingShowMoreKeys`). */
    loadingKeys: readonly string[];
    /**
     * Show-more requests the user asked for that ended with no result (failed, or cancelled by closing the panel) -
     * the caller collapses them back to their link, so clicking it again retries. A new request for the key clears it.
     */
    failedKeys: readonly string[];
    /**
     * All numbers of a street (`q="<city> <street>"`). `silent` is the prefetch of the expanded street: it shows no
     * spinner, but a later explicit request for the same street turns the spinner on while it is still running.
     */
    loadStreetNumbers: (street: Pick<IPricesSearchStreetItem, "rcn" | "streetKey">, options?: {silent?: boolean}) => void;
    /** "Pokaż więcej ulic|adresów" over a capped base response: one `q="<city> <words>"` request per city. */
    loadCityStreets: (loadingKey: string, cities: readonly IPricesSearchShowMoreCityScope[]) => void;
    /** "Pokaż więcej dzielnic": the region search again with the expanded page size. */
    loadDistricts: (query: string) => void;
    /** Cancels every request in flight (× in the field, closing the panel). Results already in stay. */
    abortAll: () => void;
}

interface IInFlightRequest {
    baseKey: string;
    scopeKey: string;
    /** Null for a silent prefetch. */
    loadingKey: string | null;
    controller: AbortController;
}

interface IShowMoreState {
    scoped: IPricesScopedAddressResult[];
    regions: {baseKey: string; result: IPricesRegionSuggestionsResult} | null;
    /** `${baseKey}|${loadingKey}` of the requests the user waits for; a key may repeat (one per city). */
    loading: string[];
    /** Loading entries whose requests all ended with nothing: rejected, or aborted while the user waited. */
    failed: string[];
    /** Loading entries with at least one answer while their other requests still run (one city of several answered). */
    answered: string[];
}

const EMPTY_STATE: IShowMoreState = {scoped: [], regions: null, loading: [], failed: [], answered: []};

const toLoadingEntry = (baseKey: string, loadingKey: string): string => `${baseKey}\u0000${loadingKey}`;

const removeOnce = (list: string[], value: string): string[] => {
    const index = list.indexOf(value);

    return index === -1 ? list : [...list.slice(0, index), ...list.slice(index + 1)];
};

const addUnique = (list: string[], value: string): string[] => (list.includes(value) ? list : [...list, value]);
const without = (list: string[], value: string): string[] => (list.includes(value) ? list.filter((item) => item !== value) : list);

// The keys of the current phrase in a list of loading entries.
const getKeysOf = (entries: readonly string[], baseKey: string | null): string[] => {
    if (baseKey === null) {
        return [];
    }

    const prefix = toLoadingEntry(baseKey, "");

    return [...new Set(entries.filter((entry) => entry.startsWith(prefix)).map((entry) => entry.slice(prefix.length)))];
};

/**
 * "Pokaż więcej" and building-number re-queries, stored with the `(baseKey, scopeKey)` they were asked
 * for - the model only accepts results of the current `baseKey`, so a slow answer for an older phrase never lands in
 * the list. A new phrase, `abortAll()` and unmounting cancel what is in flight; the address answers go through the
 * shared address cache, so asking again for the same scope costs no request. A request the user asked for that ends
 * with nothing - every request of its key failed, or it was cancelled - lands in `failedKeys`.
 */
export const usePricesSearchShowMore = (options: IUsePricesSearchShowMoreOptions): IUsePricesSearchShowMoreResult => {
    const {baseKey, source = rcnDirectAddressSource} = options;
    const [state, setState] = useState<IShowMoreState>(EMPTY_STATE);
    const inFlightRef = useRef<IInFlightRequest[]>([]);
    // Read by the stable loaders; updated before the effects of the component using the hook run.
    const baseKeyRef = useRef(baseKey);
    const sourceRef = useRef(source);

    useEffect(() => {
        baseKeyRef.current = baseKey;
        sourceRef.current = source;
    });

    const abortAll = useCallback(() => {
        const inFlight = inFlightRef.current;
        inFlightRef.current = [];
        inFlight.forEach((request) => request.controller.abort());
        // What the user was waiting for got nothing: its section goes back to the link, for another try.
        const aborted = inFlight.flatMap((request) => (request.loadingKey === null ? [] : [toLoadingEntry(request.baseKey, request.loadingKey)]));
        setState((previous) =>
            previous.loading.length === 0 && aborted.length === 0
                ? previous
                : {...previous, loading: [], answered: [], failed: aborted.reduce(addUnique, previous.failed)}
        );
    }, []);

    // A new phrase starts from nothing: older results would be ignored by the model anyway, requests are cancelled.
    useEffect(() => {
        abortAll();
        setState((previous) => (previous === EMPTY_STATE ? previous : EMPTY_STATE));
    }, [baseKey, abortAll]);

    useEffect(() => abortAll, [abortAll]);

    /** Starts a request unless the same scope is already known or on its way; returns false when nothing was started. */
    const startRequest = useCallback(
        <T>(
            scopeKey: string,
            loadingKey: string | null,
            run: (signal: AbortSignal) => Promise<T>,
            onResult: (result: T, requestBaseKey: string) => IShowMoreState | ((previous: IShowMoreState) => IShowMoreState)
        ) => {
            const requestBaseKey = baseKeyRef.current;

            if (requestBaseKey === null) {
                return;
            }

            const running = inFlightRef.current.find((request) => request.baseKey === requestBaseKey && request.scopeKey === scopeKey);

            if (running) {
                // A prefetch the user now asked for explicitly: show its spinner from here on.
                if (running.loadingKey === null && loadingKey !== null) {
                    running.loadingKey = loadingKey;
                    const entry = toLoadingEntry(requestBaseKey, loadingKey);
                    setState((previous) => ({...previous, loading: [...previous.loading, entry], failed: without(previous.failed, entry)}));
                }
                return;
            }

            const request: IInFlightRequest = {baseKey: requestBaseKey, scopeKey, loadingKey, controller: new AbortController()};
            const {signal} = request.controller;
            inFlightRef.current = [...inFlightRef.current, request];

            if (loadingKey !== null) {
                // A retry clears the failure of the previous attempt.
                const entry = toLoadingEntry(requestBaseKey, loadingKey);
                setState((previous) => ({...previous, loading: [...previous.loading, entry], failed: without(previous.failed, entry)}));
            }

            const settle = (update: ((previous: IShowMoreState) => IShowMoreState) | null) => {
                // Aborted (new phrase, abortAll, unmount): the abort already cleaned up after it.
                if (signal.aborted || !inFlightRef.current.includes(request)) {
                    return;
                }

                inFlightRef.current = inFlightRef.current.filter((item) => item !== request);
                setState((previous) => {
                    const next = update ? update(previous) : previous;
                    // Read at settle time: a silent prefetch the user asked for in the meantime counts as asked for.
                    const doneLoadingKey = request.loadingKey;

                    if (doneLoadingKey === null) {
                        return next;
                    }

                    const entry = toLoadingEntry(requestBaseKey, doneLoadingKey);
                    const loading = removeOnce(next.loading, entry);
                    const answered = update ? addUnique(next.answered, entry) : next.answered;

                    // Other cities of the same "Pokaż więcej" still run: the outcome is decided by the last one.
                    if (loading.includes(entry)) {
                        return {...next, loading, answered};
                    }

                    // One answer is enough to keep the section expanded; none collapses it back to its link.
                    const isAnswered = answered.includes(entry);

                    return {
                        ...next,
                        loading,
                        answered: without(answered, entry),
                        failed: isAnswered ? without(next.failed, entry) : addUnique(next.failed, entry)
                    };
                });
            };

            run(signal).then(
                (result) => {
                    const update = onResult(result, requestBaseKey);
                    settle(typeof update === "function" ? update : () => update);
                },
                // A failed "Pokaż więcej" keeps the rows already shown; the section collapses back to its link for another try.
                () => settle(null)
            );
        },
        []
    );

    const loadAddresses = useCallback(
        (query: string, scopeKey: string, loadingKey: string | null) => {
            const requestBaseKey = baseKeyRef.current;

            if (requestBaseKey === null) {
                return;
            }

            const currentSource = sourceRef.current;
            const cacheKey = getPricesAddressSearchCacheKey(currentSource, query, RCN_ADDRESSES_MAX_LIMIT);
            const addResult = (result: IPricesAddressSearchResult) => (previous: IShowMoreState) => ({
                ...previous,
                scoped: [
                    ...previous.scoped.filter((item) => !(item.baseKey === requestBaseKey && item.scopeKey === scopeKey)),
                    {...result, baseKey: requestBaseKey, scopeKey}
                ]
            });

            const isKnown = (previous: IShowMoreState) => previous.scoped.some((item) => item.baseKey === requestBaseKey && item.scopeKey === scopeKey);
            const cached = pricesAddressSearchCache.get(cacheKey);

            if (cached !== undefined) {
                setState((previous) => {
                    const next = isKnown(previous) ? previous : addResult(cached)(previous);

                    // A cached city answers for its "Pokaż więcej" while other cities are still asked.
                    return loadingKey === null ? next : {...next, answered: addUnique(next.answered, toLoadingEntry(requestBaseKey, loadingKey))};
                });
                return;
            }

            startRequest(
                scopeKey,
                loadingKey,
                async (signal) => {
                    const result = await currentSource.search(query, {limit: RCN_ADDRESSES_MAX_LIMIT, signal});
                    pricesAddressSearchCache.set(cacheKey, result);

                    return result;
                },
                (result) => addResult(result)
            );
        },
        [startRequest]
    );

    const loadStreetNumbers = useCallback(
        (street: Pick<IPricesSearchStreetItem, "rcn" | "streetKey">, loadOptions: {silent?: boolean} = {}) => {
            const query = getStreetScopedQuery({rawCity: street.rcn.city, rawStreet: street.rcn.street});
            loadAddresses(query, street.streetKey, loadOptions.silent ? null : street.streetKey);
        },
        [loadAddresses]
    );

    const loadCityStreets = useCallback(
        (loadingKey: string, cities: readonly IPricesSearchShowMoreCityScope[]) => {
            for (const city of cities) {
                if (city.query !== null) {
                    loadAddresses(city.query, getPricesSearchCityScopeKey(city.citySlug), loadingKey);
                }
            }
        },
        [loadAddresses]
    );

    const loadDistricts = useCallback(
        (query: string) => {
            startRequest(
                REGIONS_SCOPE_KEY,
                "districts",
                (signal) => fetchPricesRegionSuggestions(query, {pageSize: PRICES_SEARCH_REGIONS_EXPANDED_PAGE_SIZE, signal}),
                (result, requestBaseKey) => (previous) => ({...previous, regions: {baseKey: requestBaseKey, result}})
            );
        },
        [startRequest]
    );

    const loadingKeys = useMemo(() => getKeysOf(state.loading, baseKey), [baseKey, state.loading]);
    const failedKeys = useMemo(() => getKeysOf(state.failed, baseKey), [baseKey, state.failed]);
    const scoped = useMemo(() => state.scoped.filter((item) => item.baseKey === baseKey), [baseKey, state.scoped]);

    return {
        scoped,
        expandedRegions: state.regions && state.regions.baseKey === baseKey ? state.regions.result : null,
        loadingKeys,
        failedKeys,
        loadStreetNumbers,
        loadCityStreets,
        loadDistricts,
        abortAll
    };
};
